/**
 * src/components/embeds/VideoEmbed.tsx
 *
 * File purpose:
 * - Provide a small, reusable click-to-load YouTube/Vimeo embed component.
 * - Displays a thumbnail with a play overlay and only mounts the iframe after user activation.
 */

import React, { useMemo, useState } from 'react'
import { Play } from 'lucide-react'

/**
 * VideoEmbedProps
 *
 * Interface:
 * - Props for VideoEmbed component.
 */
interface VideoEmbedProps {
  /** Full iframe src or a YouTube watch/embed URL */
  src: string
  /** Accessible title for the iframe */
  title?: string
  /** Optional fixed aspect ratio percentage (default 56.25% = 16:9) */
  aspectRatio?: number
}

/**
 * extractYouTubeId
 *
 * Helper:
 * - Attempt to extract a YouTube video id from common URL formats.
 *
 * @param url - YouTube URL or embed src
 * @returns video id or null
 */
function extractYouTubeId(url: string): string | null {
  try {
    const u = url.trim()
    // If embed URL like /embed/ID
    const embedMatch = u.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{5,})/)
    if (embedMatch) return embedMatch[1]
    // If watch?v=ID
    const watchMatch = u.match(/[?&]v=([A-Za-z0-9_-]{5,})/)
    if (watchMatch) return watchMatch[1]
    // Short youtu.be/ID
    const shortMatch = u.match(/youtu\.be\/([A-Za-z0-9_-]{5,})/)
    if (shortMatch) return shortMatch[1]
    return null
  } catch {
    return null
  }
}

/**
 * VideoEmbed
 *
 * Component:
 * - Renders a thumbnail with play overlay. On click, replaces thumbnail with the actual iframe.
 *
 * Props:
 * - src: iframe src or URL
 * - title: iframe title
 * - aspectRatio: optional aspect ratio percent for container paddingTop
 */
export default function VideoEmbed({ src, title, aspectRatio = 56.25 }: VideoEmbedProps): JSX.Element {
  const [loaded, setLoaded] = useState(false)

  /** Determine YouTube id (if any) to construct thumbnail */
  const ytId = useMemo(() => extractYouTubeId(src), [src])

  /** Thumbnail url (YouTube) */
  const thumb = useMemo(() => {
    if (!ytId) return null
    return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
  }, [ytId])

  return (
    <div className="w-full rounded-md overflow-hidden bg-gray-50 border border-gray-200">
      <div style={{ position: 'relative', paddingTop: `${aspectRatio}%` }}>
        {!loaded ? (
          // Clickable poster area
          <>
            {thumb ? (
              <button
                aria-label="Play video"
                onClick={() => setLoaded(true)}
                className="absolute inset-0 w-full h-full p-0 border-0 bg-cover bg-center flex items-center justify-center"
                style={{ backgroundImage: `url(${thumb})` }}
              >
                <div className="bg-black/40 rounded-full p-3">
                  <Play className="text-white" size={28} />
                </div>
              </button>
            ) : (
              // Fallback poster when thumbnail unavailable
              <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
                <button
                  aria-label="Play video"
                  onClick={() => setLoaded(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-white/80 rounded-md"
                >
                  <Play size={16} />
                  <span className="text-sm font-medium">Play</span>
                </button>
              </div>
            )}
          </>
        ) : (
          // Actual iframe
          <iframe
            src={src}
            title={title ?? 'Video embed'}
            frameBorder={0}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          />
        )}
      </div>
    </div>
  )
}